import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api, resolveMediaUrl } from "@/lib/api";
import { STYLE_GUIDE, STYLES } from "@/lib/styleGuide";
import IdentityMediaManager from "@/components/IdentityMediaManager";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronLeft, Sparkles, Loader2, Check, Info, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";

const BUDGETS = ["Budget","Mid Range","Premium","Luxury"];

export default function AIIdentityStylePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ style_preferences: [], budget_preference: "" });
  const [saving, setSaving] = useState(false);
  const [portrait, setPortrait] = useState({
    url: user?.identity_portrait_url || null,
    confirmed: user?.identity_confirmed ?? null,
    generating: false,
  });

  useEffect(() => {
    if (!user) return;
    setForm({
      style_preferences: user.style_preferences || [],
      budget_preference: user.budget_preference || "",
    });
    setPortrait({
      url: user.identity_portrait_url || null,
      confirmed: user.identity_confirmed ?? null,
      generating: false,
    });
  }, [user]);

  const toggleStyle = (s) => setForm((f) => ({
    ...f,
    style_preferences: f.style_preferences.includes(s)
      ? f.style_preferences.filter((x) => x !== s)
      : [...f.style_preferences, s],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", {
        style_preferences: form.style_preferences,
        budget_preference: form.budget_preference || null,
      });
      await refresh();
      toast.success("Style preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const generatePortrait = async () => {
    setPortrait((p) => ({ ...p, generating: true }));
    try {
      const { data } = await api.post("/users/me/identity-portrait", {});
      setPortrait({ url: data.portrait_url, confirmed: null, generating: false });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : "Could not generate portrait");
      setPortrait((p) => ({ ...p, generating: false }));
    }
  };

  const confirmPortrait = async (accurate) => {
    try {
      await api.post("/users/me/identity-confirm", { accurate });
      setPortrait((p) => ({ ...p, confirmed: accurate }));
      toast.success(accurate ? "AI identity locked in" : "Add a clearer photo and re-run to improve");
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <button onClick={() => navigate("/profile")} className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-profile">
        <ChevronLeft size={14} /> Account
      </button>
      <p className="eyebrow mt-6 flex items-center gap-2"><Sparkles size={12} /> Stylebay · AI Identity</p>
      <h1 className="editorial-h1 mt-3">AI Identity<br/>&amp; Style.</h1>

      {/* Identity Media (photos powering AI) */}
      <div className="mt-10">
        <IdentityMediaManager />
      </div>

      {/* AI Portrait */}
      <section className="mt-6 border border-border p-6 lg:p-8" data-testid="identity-portrait-section">
        <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> AI portrait</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-lg">
          We generate a stylised portrait from your photos and Style DNA. Confirming it improves every outfit and try-on after.
        </p>
        <div className="mt-5 grid md:grid-cols-[180px_1fr] gap-6 items-start">
          <div className="aspect-[3/4] bg-muted overflow-hidden border border-border w-full">
            {portrait.generating ? (
              <div className="h-full flex flex-col items-center justify-center gap-3">
                <Loader2 size={24} className="animate-spin" />
                <p className="eyebrow">Composing</p>
              </div>
            ) : portrait.url ? (
              <img src={resolveMediaUrl(portrait.url)} alt="" data-testid="identity-portrait-img" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center px-3">
                Generate to see your AI portrait
              </div>
            )}
          </div>
          <div className="space-y-3">
            {!portrait.url && (
              <Button data-testid="identity-generate-btn" onClick={generatePortrait} disabled={portrait.generating} className="rounded-none uppercase tracking-[0.2em] text-xs h-11">
                <Sparkles size={14} className="mr-2" /> Generate my AI portrait
              </Button>
            )}
            {portrait.url && portrait.confirmed === null && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => confirmPortrait(true)} className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10" data-testid="portrait-confirm-yes">
                  <ThumbsUp size={12} className="mr-2" /> Looks like me
                </Button>
                <Button variant="outline" onClick={() => confirmPortrait(false)} className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10" data-testid="portrait-confirm-no">
                  <ThumbsDown size={12} className="mr-2" /> Not quite
                </Button>
              </div>
            )}
            {portrait.url && portrait.confirmed !== null && (
              <p className="eyebrow text-accent" data-testid="portrait-status">
                {portrait.confirmed ? "Confirmed ✓" : "Awaiting improvement"}
              </p>
            )}
            {/* Improve / Re-generate is ALWAYS visible once a portrait exists,
                even after confirmation — users can iterate freely. The old
                portrait remains visible until a new one is generated. */}
            {portrait.url && (
              <Button
                variant="outline"
                onClick={generatePortrait}
                disabled={portrait.generating}
                data-testid="portrait-improve-btn"
                className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10"
              >
                {portrait.generating ? <><Loader2 size={12} className="animate-spin mr-2" />Composing</> : <><Sparkles size={12} className="mr-2" />Improve portrait</>}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Style preferences */}
      <section className="mt-6 border border-border p-6 lg:p-8">
        <p className="eyebrow">Style preferences</p>
        <p className="text-xs text-muted-foreground mt-1">Tap the <Info size={11} className="inline -mt-0.5" /> icon for a reference look.</p>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          {STYLES.map((s) => {
            const on = form.style_preferences.includes(s);
            const ref = STYLE_GUIDE[s];
            return (
              <div key={s} className={`border transition ${on ? "border-foreground" : "border-border hover:border-foreground"}`}>
                <button onClick={() => toggleStyle(s)} className="block w-full text-left" data-testid={`style-${s}`}>
                  <div className="aspect-[5/3] overflow-hidden bg-muted relative">
                    <img src={ref.image} alt={s} className="h-full w-full object-cover" />
                    {on && <span className="absolute top-2 right-2 bg-accent text-accent-foreground px-2 py-0.5 text-[9px] uppercase tracking-[0.2em]">Selected</span>}
                  </div>
                </button>
                <div className="px-3 py-2 flex items-center justify-between">
                  <button onClick={() => toggleStyle(s)} className="text-xs uppercase tracking-[0.18em]">{s}</button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={`style-info-${s}`} aria-label={`About ${s}`} className="p-1 hover:opacity-60">
                        <Info size={12} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="rounded-none w-72">
                      <p className="font-serif text-xl">{s}</p>
                      <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{ref.description}</p>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            );
          })}
        </div>

        <p className="eyebrow mt-8">Budget</p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {BUDGETS.map((b) => (
            <button key={b} onClick={() => setForm({ ...form, budget_preference: b })}
              className={`p-4 border text-left transition ${
                form.budget_preference === b ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"
              }`} data-testid={`budget-${b.replace(/\s/g, "-").toLowerCase()}`}>
              <p className="text-xs uppercase tracking-[0.2em]">{b}</p>
            </button>
          ))}
        </div>
      </section>

      <div className="sticky bottom-20 sm:bottom-6 mt-8 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="ai-identity-save-btn"
          className="rounded-none uppercase tracking-[0.2em] text-[11px] h-11 px-8 shadow-lg"
        >
          {saving ? <><Loader2 size={14} className="animate-spin mr-2" />Saving</> : <><Check size={14} className="mr-2" />Save changes</>}
        </Button>
      </div>
    </div>
  );
}
